/**
 * ServicesMarketplace.tsx
 * Full oilfield services marketplace with tiered bidding fees
 * 
 * Fee Structure:
 * - $0 - $5,000: 5% platform fee
 * - $5,000 - $25,000: 4% platform fee  
 * - $25,000 - $100,000: 3% platform fee
 * - $100,000+: 2.5% platform fee
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Card,
  CardContent,
  Avatar,
  Badge,
  Divider,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Rating,
  InputAdornment,
  Fab,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EngineeringIcon from '@mui/icons-material/Engineering';
import HandymanIcon from '@mui/icons-material/Handyman';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import AirIcon from '@mui/icons-material/Air';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ConstructionIcon from '@mui/icons-material/Construction';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PercentIcon from '@mui/icons-material/Percent';
import GoogleIcon from '@mui/icons-material/Google';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigationIcon from '@mui/icons-material/Navigation';

import { getCyberColors } from '../theme/cyberColors';
import { telegram } from '../services/telegram';
import { feedbackService } from '../services/feedback';
import { requestPreciseLocation } from '../services/highPrecisionLocation';

const cyber = getCyberColors(true); // Default to dark mode

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type ServiceCategory = 
  | 'wellService'
  | 'hotOil'
  | 'vacuum'
  | 'trucking'
  | 'electrical'
  | 'instrumentation'
  | 'pump'
  | 'compressor'
  | 'welding'
  | 'pipeline'
  | 'crane'
  | 'dozer'
  | 'frac'
  | 'wireline'
  | 'coiledTubing'
  | 'cementing'
  | 'mudLogging'
  | 'testing'
  | 'inspection'
  | 'safety'
  | 'rental'
  | 'parts'
  | 'automotive'
  | 'diesel'
  | 'other';

export interface ServiceCompany {
  id: string;
  name: string;
  categories: ServiceCategory[];
  rating: number;
  reviewCount: number;
  verified: boolean;
  distance?: number; // miles
  responseTime?: string; // e.g., "< 1 hour"
  availability: 'available' | 'busy' | 'offline';
  phone?: string;
  email?: string;
  description: string;
  completedJobs: number;
  memberSince: string;
  avatar?: string;
}

export interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  category: ServiceCategory;
  urgency: 'emergency' | 'urgent' | 'standard' | 'flexible';
  budget?: { min: number; max: number };
  location: {
    lat: number;
    lng: number;
    address: string;
    wellName?: string;
  };
  createdAt: string;
  expiresAt: string;
  status: 'open' | 'bidding' | 'awarded' | 'completed' | 'cancelled';
  bids: ServiceBid[];
  userId: string;
  contactPhone?: string;
  photos?: string[];
}

export interface ServiceBid {
  id: string;
  companyId: string;
  companyName: string;
  amount: number;
  estimatedTime: string;
  message: string;
  createdAt: string;
  status: 'pending' | 'accepted' | 'rejected';
}

// Google Places company from API
export interface GooglePlacesCompany {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  distance: number;
  distanceDisplay: string;
  driveTime: string;
  rating?: number;
  totalRatings?: number;
  isOpen?: boolean;
  isOperational: boolean;
  icon: string;
  category: string;
  googleMapsUrl: string;
  directionsUrl: string;
  photoUrl?: string | null;
  source: 'google';
}

// Distance/radius options (in miles, converted to meters for API)
// Expanded to cover all of New Mexico and West Texas
const RADIUS_OPTIONS = [
  { label: '30 min (~25 mi)', miles: 25, meters: 40234 },
  { label: '1 hour (~50 mi)', miles: 50, meters: 80467 },
  { label: '2 hours (~100 mi)', miles: 100, meters: 160934 },
  { label: '3 hours (~150 mi)', miles: 150, meters: 241402 },
  { label: '4 hours (~200 mi)', miles: 200, meters: 321869 },
  { label: '5 hours (~250 mi)', miles: 250, meters: 402336 },
  { label: '6 hours (~300 mi)', miles: 300, meters: 482803 },
  { label: 'NM + West TX (~400 mi)', miles: 400, meters: 643738 },
];

// Google Places category mapping
const GOOGLE_CATEGORY_MAP: Record<string, string> = {
  all: 'all',
  wellService: 'wellService',
  hotOil: 'hotOil',
  vacuum: 'vacuum',
  trucking: 'trucking',
  electrical: 'electrical',
  instrumentation: 'instrumentation',
  pump: 'pump',
  compressor: 'compressor',
  welding: 'welding',
  pipeline: 'pipeline',
  crane: 'crane',
  frac: 'frac',
  wireline: 'wireline',
  rental: 'rental',
  parts: 'parts',
  safety: 'safety',
  automotive: 'automotive',
  diesel: 'diesel',
};

// ============================================================================
// CONSTANTS
// ============================================================================

const SERVICE_CATEGORIES: Record<ServiceCategory, { label: string; icon: React.ElementType; color: string }> = {
  wellService: { label: 'Well Service', icon: LocalGasStationIcon, color: '#f59e0b' },
  hotOil: { label: 'Hot Oil', icon: LocalShippingIcon, color: '#ef4444' },
  vacuum: { label: 'Vacuum Truck', icon: CleaningServicesIcon, color: '#8b5cf6' },
  trucking: { label: 'Trucking', icon: LocalShippingIcon, color: '#3b82f6' },
  electrical: { label: 'Electrical', icon: ElectricalServicesIcon, color: '#eab308' },
  instrumentation: { label: 'Instrumentation', icon: PrecisionManufacturingIcon, color: '#06b6d4' },
  pump: { label: 'Pump Service', icon: WaterDropIcon, color: '#0ea5e9' },
  compressor: { label: 'Compressor', icon: AirIcon, color: '#60a5fa' },
  welding: { label: 'Welding', icon: HandymanIcon, color: '#f97316' },
  pipeline: { label: 'Pipeline', icon: PlumbingIcon, color: '#64748b' },
  crane: { label: 'Crane Service', icon: ConstructionIcon, color: '#fbbf24' },
  dozer: { label: 'Dozer/Excavation', icon: AgricultureIcon, color: '#a855f7' },
  frac: { label: 'Frac Service', icon: FireTruckIcon, color: '#dc2626' },
  wireline: { label: 'Wireline', icon: EngineeringIcon, color: '#7c3aed' },
  coiledTubing: { label: 'Coiled Tubing', icon: EngineeringIcon, color: '#2563eb' },
  cementing: { label: 'Cementing', icon: BuildIcon, color: '#78716c' },
  mudLogging: { label: 'Mud Logging', icon: BuildIcon, color: '#92400e' },
  testing: { label: 'Testing', icon: PrecisionManufacturingIcon, color: '#0d9488' },
  inspection: { label: 'Inspection', icon: SearchIcon, color: '#4f46e5' },
  safety: { label: 'Safety', icon: VerifiedIcon, color: '#16a34a' },
  rental: { label: 'Equipment Rental', icon: InventoryIcon, color: '#ca8a04' },
  parts: { label: 'Parts & Supply', icon: WarehouseIcon, color: '#6366f1' },
  automotive: { label: 'Auto Repair', icon: LocalShippingIcon, color: '#0891b2' },
  diesel: { label: 'Diesel/Semi Repair', icon: LocalShippingIcon, color: '#0f766e' },
  other: { label: 'Other Services', icon: BuildIcon, color: '#71717a' },
};

// Note: URGENCY_OPTIONS removed - was only used by renderRequestCard which showed fake job requests

// ============================================================================
// FEE CALCULATION
// ============================================================================

/**
 * Calculate platform fee based on tiered structure
 * $0-$5K: 5%, $5K-$25K: 4%, $25K-$100K: 3%, $100K+: 2.5%
 */
export const calculatePlatformFee = (amount: number): { fee: number; rate: number; tier: string } => {
  if (amount <= 5000) {
    return { fee: amount * 0.05, rate: 5, tier: '$0 - $5K' };
  } else if (amount <= 25000) {
    return { fee: amount * 0.04, rate: 4, tier: '$5K - $25K' };
  } else if (amount <= 100000) {
    return { fee: amount * 0.03, rate: 3, tier: '$25K - $100K' };
  } else {
    return { fee: amount * 0.025, rate: 2.5, tier: '$100K+' };
  }
};

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_COMPANIES: ServiceCompany[] = [
  {
    id: '1',
    name: 'Permian Basin Well Service',
    categories: ['wellService', 'hotOil', 'vacuum'],
    rating: 4.8,
    reviewCount: 127,
    verified: true,
    distance: 12.5,
    responseTime: '< 1 hour',
    availability: 'available',
    phone: '(432) 555-0123',
    email: 'dispatch@pbwellservice.com',
    description: 'Full-service well intervention company with 24/7 dispatch',
    completedJobs: 842,
    memberSince: '2019',
  },
  {
    id: '2',
    name: 'Big Country Electric',
    categories: ['electrical', 'instrumentation'],
    rating: 4.6,
    reviewCount: 89,
    verified: true,
    distance: 8.2,
    responseTime: '< 2 hours',
    availability: 'available',
    phone: '(432) 555-0456',
    description: 'Licensed oilfield electrical contractors - control panels, VFDs, automation',
    completedJobs: 456,
    memberSince: '2020',
  },
  {
    id: '3',
    name: "Dawson's Welding & Fabrication",
    categories: ['welding', 'pipeline'],
    rating: 4.9,
    reviewCount: 203,
    verified: true,
    distance: 22.1,
    responseTime: '< 30 min',
    availability: 'busy',
    phone: '(432) 555-0789',
    description: 'API certified welding, pipeline repair, custom fabrication',
    completedJobs: 1203,
    memberSince: '2017',
  },
  {
    id: '4',
    name: 'Southwest Crane Services',
    categories: ['crane', 'trucking'],
    rating: 4.5,
    reviewCount: 67,
    verified: false,
    distance: 35.0,
    responseTime: '< 4 hours',
    availability: 'available',
    description: '25-300 ton cranes, heavy haul, rigging specialists',
    completedJobs: 234,
    memberSince: '2021',
  },
];

const MOCK_REQUESTS: ServiceRequest[] = [
  // No mock data - requires Business Plus subscription to see real job requests
];

// ============================================================================
// PROPS
// ============================================================================

interface ServicesMarketplaceProps {
  open: boolean;
  onClose: () => void;
  userLocation?: { lat: number; lng: number };
  darkMode?: boolean;
  language?: 'en' | 'es';
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ServicesMarketplace: React.FC<ServicesMarketplaceProps> = ({
  open,
  onClose,
  userLocation,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  darkMode: _darkMode = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  language: _language = 'en',
}) => {
  // State
  const [activeTab, setActiveTab] = useState<'browse' | 'requests' | 'mybids'>('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showPostRequest, setShowPostRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies] = useState<ServiceCompany[]>(MOCK_COMPANIES);
  const [requests] = useState<ServiceRequest[]>(MOCK_REQUESTS);
  const [, setSelectedCompany] = useState<ServiceCompany | null>(null);
  
  // Google Places state
  const [browseSource, setBrowseSource] = useState<'platform' | 'google'>('google'); // Default to Google
  const [googleCompanies, setGoogleCompanies] = useState<GooglePlacesCompany[]>([]);
  const [searchRadius, setSearchRadius] = useState<number>(3); // Index into RADIUS_OPTIONS (3 hours / 150 mi)
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(userLocation || null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedRequest, _setSelectedRequest] = useState<ServiceRequest | null>(null); // For future Business Plus use
  const [showBidDialog, setShowBidDialog] = useState(false);

  // Get user location on mount
  useEffect(() => {
    if (!currentLocation && open) {
      (async () => {
        try {
          const result = await requestPreciseLocation({
            desiredAccuracyMeters: 40,
            refinementTimeoutMs: 7000,
            allowLastKnownFallback: true,
            allowTelegramFallback: true,
          });

          if (result.fix) {
            setCurrentLocation({ lat: result.fix.lat, lng: result.fix.lng });
          } else {
            setCurrentLocation({ lat: 31.9973, lng: -102.0779 });
          }
        } catch (error) {
          console.error('Marketplace precise location error:', error);
          setCurrentLocation({ lat: 31.9973, lng: -102.0779 });
        }
      })();
    }
  }, [open, currentLocation]);

  // Fetch Google Places companies
  const fetchGoogleCompanies = useCallback(async () => {
    if (!currentLocation || browseSource !== 'google') return;
    
    setLoading(true);
    setGoogleError(null);
    
    try {
      const radiusOption = RADIUS_OPTIONS[searchRadius];
      const googleCategory = GOOGLE_CATEGORY_MAP[selectedCategory] || 'all';
      
      const response = await fetch(
        `/api/google/oilfield-services?lat=${currentLocation.lat}&lng=${currentLocation.lng}&category=${googleCategory}&radius=${radiusOption.meters}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch oilfield services');
      }
      
      const data = await response.json();
      setGoogleCompanies(data.places || []);
    } catch (error) {
      console.error('Google Places error:', error);
      setGoogleError(error instanceof Error ? error.message : 'Failed to search');
      setGoogleCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [currentLocation, browseSource, searchRadius, selectedCategory]);

  // Fetch when dependencies change
  useEffect(() => {
    if (open && browseSource === 'google' && currentLocation) {
      fetchGoogleCompanies();
    }
  }, [open, browseSource, currentLocation, searchRadius, selectedCategory, fetchGoogleCompanies]);

  // Filter companies based on search and category
  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const matchesSearch = searchQuery === '' || 
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || 
        company.categories.includes(selectedCategory as ServiceCategory);
      
      return matchesSearch && matchesCategory;
    });
  }, [companies, searchQuery, selectedCategory]);

  // Filter Google companies by search
  const filteredGoogleCompanies = useMemo(() => {
    if (searchQuery === '') return googleCompanies;
    return googleCompanies.filter((company) =>
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.address.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [googleCompanies, searchQuery]);

  // Filter requests
  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      const matchesSearch = searchQuery === '' || 
        request.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        request.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || 
        request.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [requests, searchQuery, selectedCategory]);

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  // Render Google Places company card
  const renderGoogleCompanyCard = (company: GooglePlacesCompany) => (
    <Card
      key={company.id}
      sx={{
        backgroundColor: cyber.bgCard,
        border: `1px solid ${cyber.borderDefault}`,
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: cyber.neonCyan,
          boxShadow: `0 0 20px ${cyber.neonCyan}30`,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header with name and Google badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: `linear-gradient(135deg, #4285f440 0%, #34a85320 100%)`,
                border: '2px solid #4285f4',
              }}
            >
              <GoogleIcon sx={{ color: '#4285f4' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: cyber.textPrimary,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {company.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {company.rating && (
                  <>
                    <Rating value={company.rating} precision={0.1} size="small" readOnly />
                    <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                      ({company.totalRatings || 0})
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
          <Chip
            size="small"
            icon={<GoogleIcon sx={{ fontSize: 14 }} />}
            label="Google"
            sx={{
              backgroundColor: '#4285f420',
              color: '#4285f4',
              fontSize: '0.65rem',
              height: 22,
            }}
          />
        </Box>

        {/* Address */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
          <LocationOnIcon sx={{ color: cyber.textMuted, fontSize: 16, mt: 0.25 }} />
          <Typography variant="body2" sx={{ color: cyber.textSecondary, fontSize: '0.8rem' }}>
            {company.address}
          </Typography>
        </Box>

        {/* Distance & Drive Time */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DirectionsCarIcon sx={{ color: cyber.neonCyan, fontSize: 16 }} />
            <Typography variant="body2" sx={{ color: cyber.neonCyan, fontWeight: 600 }}>
              {company.distanceDisplay}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ color: cyber.textMuted, fontSize: 16 }} />
            <Typography variant="body2" sx={{ color: cyber.textSecondary }}>
              ~{company.driveTime} drive
            </Typography>
          </Box>
          {company.isOpen !== undefined && (
            <Chip
              size="small"
              label={company.isOpen ? 'Open' : 'Closed'}
              sx={{
                backgroundColor: company.isOpen ? '#10b98120' : '#ef444420',
                color: company.isOpen ? '#10b981' : '#ef4444',
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: cyber.borderDefault, mb: 1.5 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Get Directions">
            <Button
              size="small"
              variant="contained"
              startIcon={<NavigationIcon />}
              onClick={() => window.open(company.directionsUrl, '_blank')}
              sx={{
                flex: 1,
                minWidth: 'auto',
                background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
                color: cyber.bgPrimary,
                fontWeight: 600,
              }}
            >
              Navigate
            </Button>
          </Tooltip>
          <Tooltip title="View on Google Maps">
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(company.googleMapsUrl, '_blank')}
              sx={{
                flex: 1,
                minWidth: 'auto',
                borderColor: '#4285f4',
                color: '#4285f4',
                '&:hover': {
                  borderColor: '#4285f4',
                  backgroundColor: '#4285f410',
                },
              }}
            >
              Google Maps
            </Button>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  // Render radius selector for Google Places
  const renderRadiusSelector = () => (
    <Box sx={{ mb: 2, px: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: cyber.textMuted }}>
          Search Radius
        </Typography>
        <Typography variant="caption" sx={{ color: cyber.neonCyan, fontWeight: 600 }}>
          {RADIUS_OPTIONS[searchRadius].label}
        </Typography>
      </Box>
      <Slider
        value={searchRadius}
        onChange={(_, value) => setSearchRadius(value as number)}
        min={0}
        max={RADIUS_OPTIONS.length - 1}
        step={1}
        marks={RADIUS_OPTIONS.map((_, idx) => ({ value: idx, label: '' }))}
        sx={{
          color: cyber.neonCyan,
          '& .MuiSlider-thumb': {
            width: 20,
            height: 20,
          },
          '& .MuiSlider-track': {
            height: 4,
          },
          '& .MuiSlider-rail': {
            height: 4,
            backgroundColor: cyber.borderDefault,
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.65rem' }}>
          25 mi
        </Typography>
        <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.65rem' }}>
          NM + West TX
        </Typography>
      </Box>
    </Box>
  );

  // Render source toggle (Platform vs Google)
  const renderSourceToggle = () => (
    <Box sx={{ mb: 2 }}>
      <ToggleButtonGroup
        value={browseSource}
        exclusive
        onChange={(_, value) => value && setBrowseSource(value)}
        fullWidth
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            color: cyber.textSecondary,
            borderColor: cyber.borderDefault,
            py: 1,
            '&.Mui-selected': {
              backgroundColor: `${cyber.neonCyan}20`,
              color: cyber.neonCyan,
              borderColor: cyber.neonCyan,
              '&:hover': {
                backgroundColor: `${cyber.neonCyan}30`,
              },
            },
          },
        }}
      >
        <ToggleButton value="google">
          <GoogleIcon sx={{ mr: 1, fontSize: 18 }} />
          Google Places
        </ToggleButton>
        <ToggleButton value="platform">
          <StorefrontIcon sx={{ mr: 1, fontSize: 18 }} />
          ZiaWellNav Partners
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderCategoryChips = () => (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      flexWrap: 'wrap',
      pb: 2,
    }}>
      <Chip
        label="All Services"
        onClick={() => setSelectedCategory('all')}
        sx={{
          backgroundColor: selectedCategory === 'all' ? cyber.neonCyan : `${cyber.neonCyan}20`,
          color: selectedCategory === 'all' ? cyber.bgPrimary : cyber.neonCyan,
          border: `1px solid ${cyber.neonCyan}40`,
          fontWeight: selectedCategory === 'all' ? 700 : 500,
          height: 40, // Glove-friendly minimum
          fontSize: '0.875rem',
        }}
      />
      {Object.entries(SERVICE_CATEGORIES).slice(0, 8).map(([key, { label, icon: Icon, color }]) => (
        <Chip
          key={key}
          icon={<Icon sx={{ fontSize: 18, color: selectedCategory === key ? cyber.bgPrimary : color }} />}
          label={label}
          onClick={() => setSelectedCategory(key as ServiceCategory)}
          sx={{
            backgroundColor: selectedCategory === key ? color : `${color}20`,
            color: selectedCategory === key ? cyber.bgPrimary : color,
            border: `1px solid ${color}40`,
            fontWeight: selectedCategory === key ? 700 : 500,
            height: 40, // Glove-friendly minimum
          }}
        />
      ))}
      <Chip
        label="More..."
        onClick={() => setShowFilters(true)}
        sx={{
          backgroundColor: `${cyber.textMuted}20`,
          color: cyber.textMuted,
          border: `1px solid ${cyber.textMuted}40`,
          height: 40, // Glove-friendly minimum
        }}
      />
    </Box>
  );

  const renderCompanyCard = (company: ServiceCompany) => {
    const CategoryIcon = SERVICE_CATEGORIES[company.categories[0]]?.icon || BuildIcon;
    const categoryColor = SERVICE_CATEGORIES[company.categories[0]]?.color || '#71717a';
    
    return (
      <Card
        key={company.id}
        onClick={() => setSelectedCompany(company)}
        sx={{
          backgroundColor: cyber.bgCard,
          border: `1px solid ${cyber.borderDefault}`,
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: categoryColor,
            boxShadow: `0 0 20px ${categoryColor}30`,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: `linear-gradient(135deg, ${categoryColor}40 0%, ${categoryColor}20 100%)`,
                  border: `2px solid ${categoryColor}60`,
                }}
              >
                <CategoryIcon sx={{ fontSize: 24, color: categoryColor }} />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ color: cyber.textPrimary, fontWeight: 600 }}>
                    {company.name}
                  </Typography>
                  {company.verified && (
                    <VerifiedIcon sx={{ fontSize: 16, color: cyber.neonGreen }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={company.rating} readOnly size="small" precision={0.1} />
                  <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                    ({company.reviewCount})
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Chip
              size="small"
              label={company.availability === 'available' ? 'Available' : company.availability === 'busy' ? 'Busy' : 'Offline'}
              sx={{
                backgroundColor: company.availability === 'available' 
                  ? `${cyber.neonGreen}20` 
                  : company.availability === 'busy' 
                    ? `${cyber.neonOrange}20` 
                    : `${cyber.textMuted}20`,
                color: company.availability === 'available' 
                  ? cyber.neonGreen 
                  : company.availability === 'busy' 
                    ? cyber.neonOrange 
                    : cyber.textMuted,
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ color: cyber.textSecondary, mb: 1.5, fontSize: '0.8rem' }}>
            {company.description}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {company.categories.slice(0, 3).map((cat) => (
              <Chip
                key={cat}
                size="small"
                label={SERVICE_CATEGORIES[cat].label}
                sx={{
                  backgroundColor: `${SERVICE_CATEGORIES[cat].color}15`,
                  color: SERVICE_CATEGORIES[cat].color,
                  fontSize: '0.65rem',
                  height: 22,
                }}
              />
            ))}
            {company.categories.length > 3 && (
              <Chip
                size="small"
                label={`+${company.categories.length - 3}`}
                sx={{
                  backgroundColor: `${cyber.textMuted}20`,
                  color: cyber.textMuted,
                  fontSize: '0.65rem',
                  height: 22,
                }}
              />
            )}
          </Box>

          <Divider sx={{ borderColor: cyber.borderDefault, mb: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {company.distance !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: cyber.textMuted }} />
                  <Typography variant="caption" sx={{ color: cyber.textSecondary }}>
                    {company.distance} mi
                  </Typography>
                </Box>
              )}
              {company.responseTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: cyber.textMuted }} />
                  <Typography variant="caption" sx={{ color: cyber.textSecondary }}>
                    {company.responseTime}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: cyber.textMuted }}>
              {company.completedJobs} jobs
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Note: renderRequestCard removed - Job Requests tab now shows Business Plus upsell

  const renderFeeInfo = () => (
    <Box sx={{ 
      p: 2, 
      backgroundColor: `${cyber.neonCyan}10`,
      borderRadius: 2,
      border: `1px solid ${cyber.neonCyan}30`,
      mb: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <PercentIcon sx={{ fontSize: 20, color: cyber.neonCyan }} />
        <Typography variant="subtitle2" sx={{ color: cyber.neonCyan, fontWeight: 600 }}>
          Platform Fee Structure
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        {[
          { tier: '$0 - $5K', rate: '5%' },
          { tier: '$5K - $25K', rate: '4%' },
          { tier: '$25K - $100K', rate: '3%' },
          { tier: '$100K+', rate: '2.5%' },
        ].map((item) => (
          <Box key={item.tier} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: cyber.textSecondary }}>
              {item.tier}
            </Typography>
            <Typography variant="caption" sx={{ color: cyber.neonGreen, fontWeight: 600 }}>
              {item.rate}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: cyber.bgPrimary,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: cyber.textPrimary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${cyber.borderDefault}`,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Services Marketplace
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: cyber.neonCyan }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: cyber.borderDefault }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              color: cyber.textSecondary,
              '&.Mui-selected': {
                color: cyber.neonCyan,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: cyber.neonCyan,
            },
          }}
        >
          <Tab label="Browse Companies" value="browse" />
          <Tab 
            label={
              <Badge badgeContent={filteredRequests.length} color="error" max={99}>
                <span>Job Requests</span>
              </Badge>
            } 
            value="requests" 
          />
          <Tab label="My Bids" value="mybids" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 2, pb: 10 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder={activeTab === 'browse' ? 'Search service companies...' : 'Search job requests...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: cyber.bgCard,
              '& fieldset': { borderColor: cyber.borderDefault },
              '&:hover fieldset': { borderColor: cyber.neonCyan },
              '&.Mui-focused fieldset': { borderColor: cyber.neonCyan },
            },
            '& .MuiInputBase-input': { color: cyber.textPrimary },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: cyber.textMuted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowFilters(!showFilters)} size="small">
                  <FilterListIcon sx={{ color: showFilters ? cyber.neonCyan : cyber.textMuted }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Category Chips */}
        {renderCategoryChips()}

        {/* Source Toggle (only on browse tab) */}
        {activeTab === 'browse' && renderSourceToggle()}

        {/* Radius Selector (only for Google source) */}
        {activeTab === 'browse' && browseSource === 'google' && renderRadiusSelector()}

        {/* Fee Info (on requests tab) */}
        {activeTab === 'requests' && renderFeeInfo()}

        {/* Content */}
        {activeTab === 'browse' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Google Places Source */}
            {browseSource === 'google' && (
              <>
                {!currentLocation ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: cyber.neonCyan, mb: 2 }} />
                    <Typography variant="body2" sx={{ color: cyber.textMuted }}>
                      Getting your location...
                    </Typography>
                  </Box>
                ) : loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: cyber.neonCyan, mb: 2 }} />
                    <Typography variant="body2" sx={{ color: cyber.textMuted }}>
                      Searching Google for oilfield services...
                    </Typography>
                  </Box>
                ) : googleError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {googleError}
                    <Button size="small" onClick={fetchGoogleCompanies} sx={{ ml: 1 }}>
                      Retry
                    </Button>
                  </Alert>
                ) : filteredGoogleCompanies.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <GoogleIcon sx={{ fontSize: 64, color: cyber.textMuted, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: cyber.textSecondary }}>
                      No oilfield services found
                    </Typography>
                    <Typography variant="body2" sx={{ color: cyber.textMuted, mb: 2 }}>
                      Try expanding your search radius or changing category
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setSearchRadius(Math.min(searchRadius + 1, RADIUS_OPTIONS.length - 1))}
                      sx={{ borderColor: cyber.neonCyan, color: cyber.neonCyan }}
                    >
                      Expand Search Radius
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Alert 
                      severity="info" 
                      icon={<GoogleIcon />}
                      sx={{ 
                        mb: 1, 
                        backgroundColor: '#4285f410',
                        '& .MuiAlert-icon': { color: '#4285f4' },
                      }}
                    >
                      Found {filteredGoogleCompanies.length} oilfield service companies within {RADIUS_OPTIONS[searchRadius].label}
                    </Alert>
                    {filteredGoogleCompanies.map(renderGoogleCompanyCard)}
                  </>
                )}
              </>
            )}

            {/* Platform Companies Source */}
            {browseSource === 'platform' && (
              <>
                {loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: cyber.neonCyan }} />
                  </Box>
                ) : filteredCompanies.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <StorefrontIcon sx={{ fontSize: 64, color: cyber.textMuted, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: cyber.textSecondary }}>
                      No partner companies found
                    </Typography>
                    <Typography variant="body2" sx={{ color: cyber.textMuted, mb: 2 }}>
                      Try Google Places to find more oilfield services
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setBrowseSource('google')}
                      startIcon={<GoogleIcon />}
                      sx={{ borderColor: '#4285f4', color: '#4285f4' }}
                    >
                      Search Google Places
                    </Button>
                  </Box>
                ) : (
                  filteredCompanies.map(renderCompanyCard)
                )}
              </>
            )}
          </Box>
        )}

        {activeTab === 'requests' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <GavelIcon sx={{ fontSize: 64, color: cyber.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: cyber.textSecondary }}>
              Job Requests
            </Typography>
            <Typography variant="body2" sx={{ color: cyber.textMuted, mb: 3 }}>
              View and bid on service requests from operators
            </Typography>
            <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
              Sign up for Business Plus ($200/mo) to see Job Requests
            </Alert>
          </Box>
        )}

        {activeTab === 'mybids' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TrendingUpIcon sx={{ fontSize: 64, color: cyber.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: cyber.textSecondary }}>
              Your Bids
            </Typography>
            <Typography variant="body2" sx={{ color: cyber.textMuted, mb: 3 }}>
              Track your submitted bids and awarded jobs
            </Typography>
            <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
              Sign up for Business Plus ($200/mo) to submit bids and appear in search results
            </Alert>
          </Box>
        )}
      </DialogContent>

      {/* FAB to post request */}
      {activeTab === 'requests' && (
        <Fab
          color="primary"
          onClick={() => setShowPostRequest(true)}
          sx={{
            position: 'fixed',
            bottom: 100,
            right: 20,
            background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Post Request Dialog - Placeholder */}
      <Dialog
        open={showPostRequest}
        onClose={() => { telegram.hapticFeedback('light'); setShowPostRequest(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgCard,
            border: `1px solid ${cyber.borderDefault}`,
          },
        }}
      >
        <DialogTitle sx={{ color: cyber.textPrimary }}>
          Post Service Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: cyber.textSecondary, mb: 2 }}>
            Create a service request to receive competitive bids from verified contractors.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Full request posting coming soon! For now, contact service companies directly.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { telegram.hapticFeedback('light'); setShowPostRequest(false); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Bid Dialog - Placeholder */}
      <Dialog
        open={showBidDialog}
        onClose={() => { telegram.hapticFeedback('light'); setShowBidDialog(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgCard,
            border: `1px solid ${cyber.borderDefault}`,
          },
        }}
      >
        <DialogTitle sx={{ color: cyber.textPrimary }}>
          Submit Bid
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Typography variant="subtitle2" sx={{ color: cyber.textSecondary, mb: 2 }}>
                {selectedRequest.title}
              </Typography>
              <TextField
                fullWidth
                label="Bid Amount ($)"
                type="number"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: cyber.textMuted }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Estimated Time"
                placeholder="e.g., 4-6 hours"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={3}
                placeholder="Describe your approach and qualifications..."
                sx={{ mb: 2 }}
              />
              {renderFeeInfo()}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { telegram.hapticFeedback('light'); setShowBidDialog(false); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowBidDialog(false);
              feedbackService.success();
              telegram.showAlert('Bid submitted! You will be notified if selected.');
            }}
            sx={{
              background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
            }}
          >
            Submit Bid
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ServicesMarketplace;
